import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Instagram, MapPin } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 70]);

  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="absolute -right-24 -top-28 font-display font-semibold text-[26rem] leading-none text-accent/30 rotate-[15deg]">C</span>
        <span className="absolute -left-20 top-1/2 font-display font-semibold text-[18rem] leading-none text-secondary rotate-[8deg] hidden lg:block">S</span>
        <span className="absolute -bottom-28 -right-6 font-display font-semibold text-[17rem] leading-none text-[#A9CDE9] rotate-[12deg] hidden xl:block">g</span>
        <span className="absolute -bottom-36 right-32 font-display font-semibold text-[20rem] leading-none text-accent -rotate-[14deg] hidden xl:block">y</span>
        <span className="absolute -bottom-24 right-64 font-display font-semibold text-[13rem] leading-none text-primary/70 rotate-[6deg] hidden xl:block">c</span>
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-secondary rounded-full px-4 py-2 mb-8"
            data-testid="hero-location-badge"
          >
            <MapPin className="w-4 h-4 text-primary" strokeWidth={2} />
            <span className="text-xs font-bold tracking-wide uppercase">Calgary's friendly cyber crew</span>
          </motion.div>

          <h1
            className="font-display font-semibold text-4xl/[1.08] sm:text-5xl/[1.08] lg:text-6xl/[1.08] tracking-tight"
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.12 }}
              >
                Cybersecurity,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-4 -mb-4">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.26 }}
              >
                without the{" "}
                <span className="relative inline-block text-primary">
                  hoodies.
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 220 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <motion.path
                      d="M3 9C60 3 160 3 217 8"
                      stroke="#F4C95D"
                      strokeWidth="6"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.7, ease: "easeOut", delay: 1.0 }}
                    />
                  </svg>
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed"
            data-testid="hero-subtext"
          >
            Plain-English online safety for everyday Calgarians — scam alerts, family tips, and
            small-business know-how. No jargon, no fear-mongering, no computer science degree required.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-newsletter"
              className="bg-primary text-primary-foreground font-display font-medium px-8 py-4 rounded-full hover:scale-[1.03] transition-transform duration-200"
            >
              Get the Weekly YYC Tip
            </a>
            <a
              href="https://www.instagram.com/cybersecyyc/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-instagram"
              className="inline-flex items-center gap-2 border-2 border-foreground font-display font-medium px-8 py-[14px] rounded-full hover:bg-foreground hover:text-background transition-colors duration-200"
            >
              <Instagram className="w-5 h-5" strokeWidth={2} />
              Follow @cybersecyyc
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8 font-hand text-2xl text-muted-foreground -rotate-2"
            data-testid="hero-handwritten-note"
          >
            no jargon, we promise!
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          <div className="hero-blob overflow-hidden border border-border shadow-sm aspect-[4/5] max-w-md mx-auto">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src="https://images.unsplash.com/photo-1758525222128-0073a5e836b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMHBlcnNvbiUyMHBob25lJTIwY29mZmVlfGVufDB8fHx8MTc4ODIyODU3OXww&ixlib=rb-4.1.0&q=85"
              alt="Everyday Calgarian checking her phone over coffee"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
          </div>
          <div
            className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground rounded-2xl px-6 py-3 shadow-sm rotate-[-2deg]"
            data-testid="hero-floating-badge"
          >
            <span className="font-display font-semibold text-sm">Keeping YYC one step ahead of scammers</span>
          </div>
        </motion.div>
      </div>

      <div className="flex justify-center pb-10">
        <a href="#scam-alerts" aria-label="Scroll to scam alerts" data-testid="hero-scroll-down">
          <ArrowDown className="w-5 h-5 text-muted-foreground animate-bounce" strokeWidth={2} />
        </a>
      </div>
    </section>
  );
}
